// src/stores/authConfigStore.js
//
// Real-time config store for session/auth behavior.
// Drives the idle-logout timer and visibility-based token refresh.
// Reads from globalSettings/authConfig with sensible defaults.

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'

const DEFAULTS = {
  enabled: true,
  idleTimeoutMinutes: 50,
  warningMinutes: 2,
  refreshOnVisibility: true,
  // How long a tab must stay hidden before its Firestore snapshots are paused
  // (dataStore/leadStore visibility handlers). Quick tab flips never tear down.
  tabSleepMinutes: 5,
}

export const useAuthConfigStore = defineStore('authConfig', () => {
  const config = ref({ ...DEFAULTS })
  const isLoading = ref(false)
  const hasFetched = ref(false)
  let _unsubscribe = null

  const enabled = computed(() => config.value.enabled !== false)
  const idleTimeoutMs = computed(
    () =>
      Math.max(1, Number(config.value.idleTimeoutMinutes) || DEFAULTS.idleTimeoutMinutes) * 60_000
  )
  const warningMs = computed(() => {
    const warn = Math.max(0, Number(config.value.warningMinutes) || 0) * 60_000
    return Math.min(warn, idleTimeoutMs.value - 1_000)
  })
  const refreshOnVisibility = computed(() => config.value.refreshOnVisibility !== false)
  const tabSleepMs = computed(() => {
    const raw = Number(config.value.tabSleepMinutes)
    // 0 is valid (tear down the instant the tab hides — the legacy behavior);
    // negative/NaN falls back to the default.
    const mins = Number.isFinite(raw) && raw >= 0 ? raw : DEFAULTS.tabSleepMinutes
    return mins * 60_000
  })

  function subscribe(options = {}) {
    if (options.force && _unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
      hasFetched.value = false
    }
    if (_unsubscribe) return
    isLoading.value = true
    const ref_ = doc(db, 'globalSettings', 'authConfig')
    _unsubscribe = onSnapshot(
      ref_,
      snap => {
        config.value = snap.exists() ? { ...DEFAULTS, ...snap.data() } : { ...DEFAULTS }
        hasFetched.value = true
        isLoading.value = false
      },
      err => {
        console.error('[authConfigStore] onSnapshot error:', err)
        config.value = { ...DEFAULTS }
        hasFetched.value = true
        isLoading.value = false
      }
    )
  }

  function unsubscribeAll() {
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }
    hasFetched.value = false
  }

  async function saveConfig(next) {
    const payload = {
      enabled: next.enabled !== false,
      idleTimeoutMinutes: Math.max(
        1,
        Number(next.idleTimeoutMinutes) || DEFAULTS.idleTimeoutMinutes
      ),
      warningMinutes: Math.max(0, Number(next.warningMinutes) || 0),
      refreshOnVisibility: next.refreshOnVisibility !== false,
      updatedAt: serverTimestamp(),
    }
    await setDoc(doc(db, 'globalSettings', 'authConfig'), payload, { merge: true })
  }

  return {
    config,
    isLoading,
    hasFetched,
    enabled,
    idleTimeoutMs,
    warningMs,
    refreshOnVisibility,
    tabSleepMs,
    subscribe,
    unsubscribeAll,
    saveConfig,
  }
})
